import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";

export const PromotionBannerCarousel = ({ promotions = [] }) => {
  const navigate = useNavigate();
  const banners = promotions.filter(
    (promotion) => promotion.display_format === "banner" && promotion.banner_image_url,
  );
  const [index, setIndex] = useState(0);
  const seen = useRef(new Set());
  const active = banners[index];

  useEffect(() => {
    setIndex((current) => Math.min(current, Math.max(banners.length - 1, 0)));
  }, [banners.length]);

  useEffect(() => {
    if (banners.length < 2) {
      return undefined;
    }
    const interval = window.setInterval(() => {
      setIndex((current) => (current + 1) % banners.length);
    }, 6000);
    return () => window.clearInterval(interval);
  }, [banners.length]);

  useEffect(() => {
    if (active && !seen.current.has(active.id)) {
      seen.current.add(active.id);
      api.post(`/promotions/${active.id}/view`).catch(() => {});
    }
  }, [active]);

  if (!active) {
    return null;
  }

  const openOffer = () => {
    api.post(`/promotions/${active.id}/click`).catch(() => {});
    const destination = active.banner_destination === "inventory"
      ? "/inventory"
      : active.linked_vehicle_id
        ? `/vehicles/${active.linked_vehicle_id}`
        : "/inventory";
    navigate(destination);
  };
  const previous = () => setIndex((current) => (current - 1 + banners.length) % banners.length);
  const next = () => setIndex((current) => (current + 1) % banners.length);

  return (
    <section
      className="relative overflow-hidden bg-slate-950"
      data-testid="promotion-banner-carousel"
    >
      <button
        type="button"
        onClick={openOffer}
        className={
          "group relative block w-full text-left focus:outline-none focus:ring-2 focus:ring-rose-500"
        }
        data-testid={`promotion-banner-${active.id}`}
      >
        <img
          src={active.banner_image_url}
          alt=""
          className={
            "aspect-[3/1] w-full object-contain transition-transform duration-700 " +
            "group-hover:scale-[1.015]"
          }
        />
        {!active.banner_hide_overlay && (
          <>
            <div
              className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/40 to-transparent"
            />
            <div
              className="absolute inset-y-0 left-0 flex max-w-[72%] flex-col justify-center px-5 sm:px-10"
            >
              <p
                className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-rose-300 sm:text-xs"
              >
                Broker-selected offer
              </p>
              <h2
                className="mt-1 line-clamp-2 font-display text-base font-black uppercase text-white sm:text-3xl"
              >
                {active.title}
              </h2>
              <span
                className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-white sm:text-sm"
              >
                View offer <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </>
        )}
      </button>
      {banners.length > 1 && (
        <div className="absolute bottom-3 right-3 flex items-center gap-2 sm:bottom-5 sm:right-5">
          <button
            type="button"
            onClick={previous}
            className={
              "flex h-8 w-8 items-center justify-center border border-white/30 " +
              "bg-slate-950/70 text-white transition-colors hover:bg-slate-800"
            }
            aria-label="Previous banner"
            data-testid="promotion-banner-previous"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="font-mono text-[10px] font-bold text-white">
            {index + 1}/{banners.length}
          </span>
          <button
            type="button"
            onClick={next}
            className={
              "flex h-8 w-8 items-center justify-center border border-white/30 " +
              "bg-slate-950/70 text-white transition-colors hover:bg-slate-800"
            }
            aria-label="Next banner"
            data-testid="promotion-banner-next"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </section>
  );
};